import React, { useState, useEffect } from 'react';
import api from '../../api/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTrophy,
  faMedal,
  faAward,
  faStar,
  faLock,
  faUnlock,
  faGem,
  faCrown,
  faPlane,
  faGlobe,
  faChartLine,
  faCalendarCheck,
  faSpinner,
  faQuestion
} from '@fortawesome/free-solid-svg-icons';
import './CareerCardFramework.css';
import './Achievements.css';

const Achievements = () => {
  const [achievements, setAchievements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categoryIcons = {
    SPECIAL: faCrown,
    LANDING: faPlane,
    EXPLORATION: faGlobe,
    MONTHLY: faCalendarCheck
  };

  const rarityConfig = {
    common: {
      color: '#94a3b8',
      icon: faStar,
      label: 'Común'
    },
    rare: {
      color: '#3b82f6',
      icon: faGem,
      label: 'Raro'
    },
    epic: {
      color: '#8b5cf6',
      icon: faMedal,
      label: 'Épico'
    },
    legendary: {
      color: '#f59e0b',
      icon: faCrown,
      label: 'Legendario'
    }
  };

  useEffect(() => {
    fetchAchievements();
  }, []);

  const fetchAchievements = async () => {
    try {
      const response = await api.get('/career/achievements');
      setAchievements(response.data);
    } catch (error) {
      console.error('Error fetching achievements:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="achievements-loading">
        <FontAwesomeIcon icon={faSpinner} spin size="3x" />
        <p>Cargando logros...</p>
      </div>
    );
  }

  if (!achievements) {
    return (
      <div className="achievements-error">
        <p>Error al cargar los logros</p>
      </div>
    );
  }

  const { totalAchievements, unlockedCount, completionPercentage, categories, recentUnlocks } = achievements;

  const displayCategories = selectedCategory === 'all' 
    ? Object.entries(categories)
    : Object.entries(categories).filter(([cat]) => cat === selectedCategory);

  return (
    <div className="achievements-container">
      <div className="achievements-header">
        <div className="career-stats-grid">
          <div className="career-stat-item">
            <FontAwesomeIcon icon={faTrophy} style={{ fontSize: '2rem', color: '#10b981', marginBottom: '0.5rem' }} />
            <span className="career-stat-value">{unlockedCount}</span>
            <span className="career-stat-label">Desbloqueados</span>
          </div>
          <div className="career-stat-item">
            <FontAwesomeIcon icon={faAward} style={{ fontSize: '2rem', color: '#3b82f6', marginBottom: '0.5rem' }} />
            <span className="career-stat-value">{totalAchievements}</span>
            <span className="career-stat-label">Total de Logros</span>
          </div>
          <div className="career-stat-item">
            <div style={{ position: 'relative', width: '80px', height: '80px', marginBottom: '0.5rem' }}>
              <svg viewBox="0 0 36 36" style={{ width: '80px', height: '80px', transform: 'rotate(-90deg)' }}>
                <defs>
                  <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="var(--accent-primary)" />
                    <stop offset="100%" stopColor="var(--accent-secondary)" />
                  </linearGradient>
                </defs>
                <path
                  className="circle-bg"
                  d="M18 2.0845
                    a 15.9155 15.9155 0 0 1 0 31.831
                    a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="var(--background-secondary)"
                  strokeWidth="2.8"
                />
                <path
                  className="circle"
                  strokeDasharray={`${completionPercentage}, 100`}
                  d="M18 2.0845
                    a 15.9155 15.9155 0 0 1 0 31.831
                    a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="url(#gradient)"
                  strokeWidth="2.8"
                  strokeLinecap="round"
                />
              </svg>
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: '1.25rem',
                fontWeight: '700',
                color: 'var(--text-primary)'
              }}>
                {completionPercentage}%
              </div>
            </div>
            <span className="career-stat-label">Completado</span>
          </div>
        </div>

        {recentUnlocks && recentUnlocks.length > 0 && (
          <div className="recent-unlocks">
            <h3>Logros Recientes</h3>
            <div className="recent-unlocks-list">
              {recentUnlocks.map((achievement) => (
                <div key={achievement.id} className="recent-unlock-item">
                  <div className={`achievement-icon ${achievement.rarity}`}>
                    <FontAwesomeIcon icon={categoryIcons[achievement.category] || faTrophy} />
                  </div>
                  <div className="unlock-info">
                    <span className="unlock-name">{achievement.name}</span>
                    <span className="unlock-points">+{achievement.points} pts</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="achievements-filters">
        <button
          className={`filter-btn ${selectedCategory === 'all' ? 'active' : ''}`}
          onClick={() => setSelectedCategory('all')}
        >
          <FontAwesomeIcon icon={faChartLine} />
          Todos
        </button>
        {Object.keys(categories).map((category) => (
          <button
            key={category}
            className={`filter-btn ${selectedCategory === category ? 'active' : ''}`}
            onClick={() => setSelectedCategory(category)}
          >
            <FontAwesomeIcon icon={categoryIcons[category] || faTrophy} />
            {category.charAt(0) + category.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      <div className="achievements-grid">
        {displayCategories.map(([category, achievementsList]) => (
          <div key={category} className="career-section">
            <div className="career-section-header">
              <h2 className="career-section-title">
                <FontAwesomeIcon icon={categoryIcons[category] || faTrophy} />
                {category.charAt(0) + category.slice(1).toLowerCase()}
              </h2>
              <span className="career-card-badge">
                {achievementsList.filter(a => a.unlocked).length} / {achievementsList.length}
              </span>
            </div>

            <div className="career-cards-grid cols-3">
              {achievementsList.map((achievement) => (
                <div
                  key={achievement.id}
                  className={`career-card ${
                    achievement.unlocked ? 'completed' : 'locked'
                  } rarity-${achievement.rarity} ${
                    category === 'LANDING' ? 'disabled' : ''
                  }`}
                  style={achievement.unlocked ? {} : { opacity: 0.7 }}
                >
                  <div className="career-card-glow" />
                  
                  <div className="career-card-header">
                    <div 
                      className="career-card-icon large"
                      style={{
                        background: achievement.rarity === 'legendary' 
                          ? 'linear-gradient(135deg, #f59e0b, #fbbf24)'
                          : achievement.rarity === 'epic'
                          ? 'linear-gradient(135deg, #8b5cf6, #a78bfa)'
                          : achievement.rarity === 'rare'
                          ? 'linear-gradient(135deg, #3b82f6, #60a5fa)'
                          : 'linear-gradient(135deg, #94a3b8, #cbd5e1)'
                      }}
                    >
                      {achievement.isSecret && !achievement.unlocked ? (
                        <FontAwesomeIcon icon={faQuestion} />
                      ) : (
                        <FontAwesomeIcon icon={categoryIcons[category] || faTrophy} />
                      )}
                      {achievement.unlocked && (
                        <span style={{
                          position: 'absolute',
                          top: '-5px',
                          right: '-5px',
                          background: 'var(--background-primary)',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '2px solid #10b981'
                        }}>
                          <FontAwesomeIcon
                            icon={faUnlock}
                            style={{ color: '#10b981', fontSize: '0.75rem' }}
                          />
                        </span>
                      )}
                      {!achievement.unlocked && (
                        <span style={{
                          position: 'absolute',
                          top: '-5px',
                          right: '-5px',
                          background: 'var(--background-primary)',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '1px solid var(--background-secondary)'
                        }}>
                          <FontAwesomeIcon
                            icon={faLock}
                            style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}
                          />
                        </span>
                      )}
                    </div>
                    <div 
                      className="career-card-badge"
                      style={{
                        background: achievement.rarity === 'legendary'
                          ? 'rgba(245, 158, 11, 0.1)'
                          : achievement.rarity === 'epic'
                          ? 'rgba(139, 92, 246, 0.1)'
                          : achievement.rarity === 'rare'
                          ? 'rgba(59, 130, 246, 0.1)'
                          : 'rgba(148, 163, 184, 0.1)',
                        color: achievement.rarity === 'legendary'
                          ? '#f59e0b'
                          : achievement.rarity === 'epic'
                          ? '#8b5cf6'
                          : achievement.rarity === 'rare'
                          ? '#3b82f6'
                          : '#94a3b8'
                      }}
                    >
                      <FontAwesomeIcon icon={rarityConfig[achievement.rarity].icon} />
                      <span>{rarityConfig[achievement.rarity].label}</span>
                    </div>
                  </div>
                  
                  <div className="career-card-content">
                    <h3 className="career-card-title">
                      {achievement.isSecret && !achievement.unlocked
                        ? '???'
                        : achievement.name}
                    </h3>
                    <p className="career-card-description">
                      {achievement.isSecret && !achievement.unlocked
                        ? 'Logro secreto - Desbloquéalo para revelar'
                        : category === 'LANDING'
                        ? 'Próximamente - Disponible cuando se active ACARS'
                        : achievement.description}
                    </p>
                  </div>
                  
                  <div className="career-card-footer">
                    <div className="career-card-points">
                      <FontAwesomeIcon icon={faStar} />
                      <span>{achievement.points} puntos</span>
                    </div>
                    {achievement.unlocked && achievement.unlockedAt && (
                      <div className="career-card-date">
                        {new Date(achievement.unlockedAt).toLocaleDateString('es-ES')}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Achievements;